import { useState } from 'react';
import { useSessionPicture } from './session-picture';

// An App card's 16:10 tile. The App's name on a soft tint is painted at once;
// the App's own picture (small WebP thumbnails, cached by the browser) covers it
// when it loads. A saved picture's signed URL carries no credentials, so the web
// and the native shells load it the same way; a cached view's picture is read
// with the session (useSessionPicture).

/** The larger rendition: a cached view's picture names its size in the path, a saved picture in the query. */
const twice = (src: string) => (src.endsWith('/1x') ? `${src.slice(0, -3)}/2x` : `${src}?size=2x`);

/** The App's own picture over its name tile, or the name tile alone. */
export function AppCardPreview({ title, src: requested }: { title: string; src: string | null }) {
  const { src, fetched } = useSessionPicture(requested);
  const [failed, setFailed] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<string | null>(null);
  // A different picture after one has shown (a refreshed cached view) is never shown over the old one: the name
  // tile shows until the new picture has loaded, then it fades in. Pending (no src) is the name tile alone.
  const replacing = loaded !== null && loaded !== src;
  return (
    <span className="ap-app-card-preview" aria-hidden="true">
      <span className="ap-app-card-placeholder">
        <span>{title}</span>
      </span>
      {src && failed !== src && (
        <img
          key={src}
          src={src}
          // A fetched picture is one rendition: the 1x covers a card at up to half a phone's width.
          srcSet={fetched ? undefined : `${src} 640w, ${twice(src)} 1280w`}
          sizes={fetched ? undefined : '(max-width: 640px) 50vw, 320px'}
          width={640}
          height={400}
          loading="lazy"
          decoding="async"
          alt=""
          data-fade={replacing ? 'true' : undefined}
          data-ready={loaded === src ? 'true' : undefined}
          onLoad={() => setLoaded(src)}
          onError={() => setFailed(src)}
        />
      )}
    </span>
  );
}
